import React from 'react';
import type { LucideIcon } from 'lucide-react';
import type { TripTileTheme } from './tripTileThemes';

type Props = {
  selected?: boolean;
  onSelect?: () => void;
  theme: TripTileTheme;
  language?: string;
  origin?: React.ReactNode;
  destination?: React.ReactNode;
  routeIcon: LucideIcon;
  distanceLabel?: string | null;
  durationLabel?: string | null;
  /** Replaces the default 3-column route grid (e.g. car vertical stops). */
  routeBody?: React.ReactNode;
  showEndpointLabels?: boolean;
  meta: React.ReactNode;
  actions?: React.ReactNode;
  actionsDirection?: 'row' | 'column';
  extra?: React.ReactNode;
  after?: React.ReactNode;
  className?: string;
};

export const TripRouteTile: React.FC<Props> = ({
  selected = false,
  onSelect,
  theme,
  language = 'ru-RU',
  origin,
  destination,
  routeIcon: RouteIcon,
  distanceLabel,
  durationLabel,
  routeBody,
  showEndpointLabels = true,
  meta,
  actions,
  actionsDirection = 'column',
  extra,
  after,
  className = '',
}) => {
  const useRu = language.toLowerCase().startsWith('ru');
  const showActions = selected && Boolean(actions);
  const actionsWrapClass =
    actionsDirection === 'column'
      ? `flex flex-col items-center gap-0.5 overflow-hidden transition-all duration-200 ${
          showActions ? 'max-w-[2.25rem] opacity-100' : 'max-w-0 opacity-0'
        }`
      : `flex flex-row items-center gap-0.5 overflow-hidden transition-all duration-200 ${
          showActions ? 'max-w-[7.5rem] opacity-100' : 'max-w-0 opacity-0'
        }`;

  const defaultRouteGrid = (
    <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3">
      <div className="min-w-0">
        {showEndpointLabels ? (
          <p className="text-[10px] uppercase tracking-wider text-stone-400 dark:text-slate-500 mb-0.5">
            {useRu ? 'Откуда' : 'From'}
          </p>
        ) : null}
        {origin}
      </div>

      <div className="flex flex-col items-center justify-center px-1 min-w-[4.5rem] sm:min-w-[5.5rem]">
        <div className="flex items-center gap-1.5 w-full max-w-[7rem]">
          <span className={`h-2 w-2 rounded-full shrink-0 ${theme.routeDotFrom}`} />
          <span className={`flex-1 border-t border-dashed ${theme.routeLine}`} />
          <RouteIcon className={`h-3.5 w-3.5 shrink-0 ${theme.routeIcon}`} />
          <span className={`flex-1 border-t border-dashed ${theme.routeLine}`} />
          <span className={`h-2 w-2 rounded-full shrink-0 ${theme.routeDotTo}`} />
        </div>
        {distanceLabel ? (
          <span className={`text-sm font-semibold mt-1.5 whitespace-nowrap tabular-nums ${theme.distance ?? theme.price}`}>
            {distanceLabel}
          </span>
        ) : null}
        {durationLabel ? (
          <span className="text-[11px] text-stone-500 dark:text-slate-400 mt-0.5 tabular-nums text-center">
            {durationLabel}
          </span>
        ) : null}
      </div>

      <div className="min-w-0 text-right">
        {showEndpointLabels ? (
          <p className="text-[10px] uppercase tracking-wider text-stone-400 dark:text-slate-500 mb-0.5">
            {useRu ? 'Куда' : 'To'}
          </p>
        ) : null}
        {destination}
      </div>
    </div>
  );

  return (
    <div
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (onSelect && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onSelect();
        }
      }}
      className={`group relative rounded-2xl border transition-all overflow-hidden mb-3 ${onSelect ? 'cursor-pointer' : ''} ${
        selected
          ? `${theme.selectedBorder} ${theme.selectedShadow}`
          : `${theme.softBorder} shadow-sm hover:shadow-md ${theme.hoverBorder}`
      } bg-white dark:bg-slate-800/90 ${className}`}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${theme.stripe}`} />
      <div className="pl-4 sm:pl-5 pr-3.5 sm:pr-4 py-3.5 sm:py-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
          {routeBody ?? defaultRouteGrid}

          <div className="lg:w-[16rem] shrink-0 flex items-start justify-end gap-1 pt-2 lg:pt-0 border-t lg:border-t-0 border-stone-200/70 dark:border-slate-600/60">
            <div className="min-w-0 lg:text-right space-y-1">{meta}</div>
            {actions ? (
              <div className={`shrink-0 ${actionsWrapClass}`}>{actions}</div>
            ) : null}
          </div>
        </div>
        {extra}
      </div>
      {after}
    </div>
  );
};
